/* ============================================================================
   FULLSCREEN IMAGE VIEWING ON A POINTER DEVICE.

   ── WHAT WAS ACTUALLY WRONG, MEASURED BEFORE ANYTHING WAS WRITTEN ─────────
   Three separate faults, two of them Horizon's and one of them ours.

   1. OURS. `rug-pdp.css`'s fold rule read
      `media-gallery .product-media-container.constrain-height`, and the zoom
      dialog is INSIDE `<media-gallery>` — `rug-media-gallery-content.liquid`
      builds every zoomed slide from the same `product_media_container_class`,
      so all of them carry `.constrain-height` too. Measured at 390x844: the
      dialog was 390x844 and the slide inside it was 390x386, so a fullscreen
      image occupied 46% of the screen with the rest empty. Horizon's own rules
      in that file all carry `:not(.dialog-zoomed-gallery *)` for exactly this
      reason and ours did not. Fixed in the stylesheet, not here.

   2. HORIZON'S, AND IT IS WHY DESKTOP HAS NO ZOOM AT ALL.
      `assets/drag-zoom-wrapper.js` opens `connectedCallback` with
          if (!isMobileBreakpoint()) return;
      before binding anything, and the only listeners it ever binds are
      `touchstart`/`touchmove`/`touchend`. There is no wheel handler, no
      pointerdown handler and no dblclick handler in the file. So above the
      mobile breakpoint the element is inert: no zoom, no pan, nothing.

   3. HORIZON'S. The zoomed `<li>` carries `on:click="/close"`, so on a device
      with a mouse the only thing a click on the image can do is shut the
      dialog. "Click to zoom" was not merely missing — it was actively taken.

   ── WHAT THIS FILE DOES, AND WHAT IT DELIBERATELY DOES NOT ────────────────
   It adds wheel-to-zoom, click-to-zoom and drag-to-pan on POINTER devices
   only, driving the SAME three custom properties Horizon's wrapper already
   drives — `--drag-zoom-scale`, `--drag-zoom-translate-x/y` — which the
   stylesheet in `rug-media-gallery-content.liquid` composes into one
   `transform`. Nothing here invents a second transform, so the two cannot
   disagree about where the image is.

   IT DOES NOTHING ON TOUCH. Horizon's wrapper already handles pinch, drag and
   double-tap correctly once the frame is the right size, and fault 1 above was
   the whole of the mobile problem. Binding a second set of gesture handlers
   over a working one is how two implementations come to fight for the same
   pointer. `matchMedia('(hover: hover) and (pointer: fine)')` is the test: it
   asks what the INPUT can do rather than how wide the window is, so a touch
   laptop keeps the pinch it can actually perform and a narrow desktop window
   keeps its wheel.

   THE CLOSE PATHS ARE UNCHANGED AND ARE NOT REIMPLEMENTED HERE — the X button,
   Escape and a click outside the image are all Horizon's. What changes is that
   a click ON the image is consumed by the zoom instead of reaching the `<li>`'s
   `/close`, so the customer can still close by clicking the empty space either
   side of a contained image, which is the ordinary lightbox convention.

   ── THE SCALE FLOOR IS 1 AND THE ROOF IS 5 ───────────────────────────────
   Read from `drag-zoom-wrapper.js` (`MIN_ZOOM`, `MAX_ZOOM`, `DEFAULT_ZOOM`)
   rather than chosen, so the two paths cannot end up with different limits.
   They are restated as literals here because a theme asset cannot import from
   another asset without becoming a module, and a module is what Shopify's
   bundler turns into `require()` in this theme — a defect this project has
   already paid for once. The regression test asserts the two files agree.
   ========================================================================== */
(function () {
  'use strict';

  var MIN_ZOOM = 1;
  var MAX_ZOOM = 5;
  var STEP_ZOOM = 2;          /* what one click gets you */
  var WHEEL_RATE = 0.0022;    /* deltaY -> scale, tuned so a notch is ~10% */
  var DRAG_SLOP = 4;          /* px before a press counts as a drag, not a click */

  /* TWO CONDITIONS, AND THE SECOND IS NOT REDUNDANT.
   *
   * `(hover: hover) and (pointer: fine)` asks what the INPUT can do rather
   * than how wide the window is, so a touch laptop keeps the pinch it can
   * actually perform and a narrow desktop window keeps its wheel.
   *
   * `(min-width: 750px)` IS HORIZON'S OWN `isMobileBreakpoint()`, read from
   * `assets/utilities.js` (`mediaQueryLarge`) rather than chosen — it is the
   * exact test `drag-zoom-wrapper.js` uses to decide whether to bind its touch
   * handlers, so this is the one condition that guarantees the two modules
   * never bind to the same element. Without it a touch-capable laptop in a
   * narrow window satisfies BOTH (its trackpad is a fine pointer AND the
   * window is below 750), Horizon binds touch, this binds pointer, and a
   * single finger drives both — which is the fight this file's own comment
   * says it exists to avoid. Measured in headless Chrome at 390px, which
   * reports a fine pointer because it has no touchscreen: before this line
   * the wrapper carried `data-rz-zoom` AND Horizon's 1.5x default zoom at
   * once.
   *
   * THE TWO ARE EXCLUSIVE BY CONSTRUCTION, NOT BY AGREEMENT: below 750 only
   * Horizon binds, at or above 750 only this does, and above 750 Horizon has
   * already returned before binding anything. */
  function pointerDevice() {
    try {
      return window.matchMedia('(hover: hover) and (pointer: fine)').matches
          && window.matchMedia('(min-width: 750px)').matches;
    } catch (e) {
      return false;
    }
  }

  function clamp(n, lo, hi) { return n < lo ? lo : n > hi ? hi : n; }

  /* THE TRANSLATION IS CONSTRAINED SO THE IMAGE CANNOT BE DRAGGED OFF SCREEN.
   * The wrapper's stylesheet applies `scale()` and THEN `translate()`, and a
   * translate after a scale is itself scaled — so the usable travel in CSS
   * pixels is half the overflow DIVIDED BY the scale, not half the overflow.
   * Getting that wrong lets the rug be flung entirely out of the frame at 5x,
   * which is exactly what "drag to pan" must not do. */
  function limits(el, scale) {
    var img = el.querySelector('img');
    if (!img) return { x: 0, y: 0 };
    var w = img.clientWidth, h = img.clientHeight;
    return {
      x: Math.max(0, (w * scale - el.clientWidth) / 2 / scale),
      y: Math.max(0, (h * scale - el.clientHeight) / 2 / scale)
    };
  }

  function paint(el, st) {
    var lim = limits(el, st.scale);
    st.x = clamp(st.x, -lim.x, lim.x);
    st.y = clamp(st.y, -lim.y, lim.y);
    el.style.setProperty('--drag-zoom-scale', String(st.scale));
    el.style.setProperty('--drag-zoom-translate-x', st.x + 'px');
    el.style.setProperty('--drag-zoom-translate-y', st.y + 'px');
    if (st.scale > MIN_ZOOM + 0.001) el.setAttribute('data-rz-zoomed', '');
    else el.removeAttribute('data-rz-zoomed');
  }

  /* ZOOMING KEEPS THE POINT UNDER THE CURSOR STILL, which is the whole
   * difference between a zoom that feels like a magnifier and one that feels
   * like the picture jumping. The cursor is measured from the element's own
   * centre because the transform origin is the centre. */
  function zoomAt(el, st, next, clientX, clientY) {
    var r = el.getBoundingClientRect();
    var cx = clientX - (r.left + r.width / 2);
    var cy = clientY - (r.top + r.height / 2);
    var before = st.scale;
    st.scale = clamp(next, MIN_ZOOM, MAX_ZOOM);
    if (st.scale === MIN_ZOOM) { st.x = 0; st.y = 0; paint(el, st); return; }
    /* The content point under the cursor is (c/before - t). Hold it fixed. */
    st.x += cx / st.scale - cx / before;
    st.y += cy / st.scale - cy / before;
    paint(el, st);
  }

  function attach(el) {
    if (el.hasAttribute('data-rz-zoom')) return;
    el.setAttribute('data-rz-zoom', '');
    var st = { scale: MIN_ZOOM, x: 0, y: 0 };
    var press = null;
    paint(el, st);

    el.addEventListener('wheel', function (e) {
      e.preventDefault();
      /* A trackpad pinch arrives as a wheel event with ctrlKey set; both it
       * and an ordinary wheel mean the same thing here, so neither is
       * special-cased. */
      zoomAt(el, st, st.scale * Math.exp(-e.deltaY * WHEEL_RATE), e.clientX, e.clientY);
    }, { passive: false });

    el.addEventListener('pointerdown', function (e) {
      if (e.button !== 0) return;
      press = { x: e.clientX, y: e.clientY, sx: st.x, sy: st.y, moved: false, id: e.pointerId };
      el.setPointerCapture(e.pointerId);
    });

    el.addEventListener('pointermove', function (e) {
      if (!press || e.pointerId !== press.id) return;
      var dx = e.clientX - press.x, dy = e.clientY - press.y;
      if (!press.moved && Math.abs(dx) + Math.abs(dy) < DRAG_SLOP) return;
      press.moved = true;
      el.setAttribute('data-rz-dragging', '');
      if (st.scale <= MIN_ZOOM) return;      /* nothing to pan at 1x */
      st.x = press.sx + dx / st.scale;
      st.y = press.sy + dy / st.scale;
      paint(el, st);
    });

    function release(e) {
      if (!press || (e && e.pointerId !== press.id)) return;
      var wasDrag = press.moved;
      press = null;
      el.removeAttribute('data-rz-dragging');
      if (wasDrag) return;
      /* A CLICK TOGGLES. At 1x it zooms to STEP_ZOOM under the cursor; at any
       * zoom it returns to 1x. Two states rather than a ladder, because a
       * ladder needs a way back down and the only obvious one is the same
       * click. */
      zoomAt(el, st, st.scale > MIN_ZOOM + 0.001 ? MIN_ZOOM : STEP_ZOOM,
             e.clientX, e.clientY);
    }
    el.addEventListener('pointerup', release);
    el.addEventListener('pointercancel', function () {
      press = null; el.removeAttribute('data-rz-dragging');
    });

    /* THE `<li>` ABOVE CARRIES `on:click="/close"`. Without this the click that
     * just zoomed also shuts the dialog, which is fault 3. It is stopped HERE,
     * on the wrapper, so a click on the empty space beside a contained image
     * still reaches the li and still closes — the ordinary lightbox behaviour,
     * and the only close path this file touches. */
    el.addEventListener('click', function (e) { e.stopPropagation(); });

    /* Reset when the dialog shuts, so the next open starts at 1x rather than
     * wherever the last customer left it. `close` bubbles from <dialog>. */
    var dlg = el.closest('dialog');
    if (dlg) dlg.addEventListener('close', function () {
      st.scale = MIN_ZOOM; st.x = 0; st.y = 0; paint(el, st);
    });
  }

  function scan(root) {
    var nodes = (root || document).querySelectorAll('drag-zoom-wrapper');
    for (var i = 0; i < nodes.length; i++) attach(nodes[i]);
  }

  function boot() {
    scan(document);
    /* The gallery is re-inserted on every variant change (media-gallery.js
     * does `this.replaceWith(newMediaGallery)`), so the wrappers that exist at
     * load are not the ones that exist later. An observer is the only thing
     * that survives that; `data-rz-zoom` makes re-attachment a no-op. */
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType !== 1) continue;
          if (n.tagName === 'DRAG-ZOOM-WRAPPER') attach(n);
          else if (n.querySelector) scan(n);
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  /* THE BREAKPOINT CAN BE CROSSED AFTER LOAD — a rotation, a resized window,
   * a devtools panel opening. `boot` is idempotent (`data-rz-zoom` makes
   * `attach` a no-op and the observer is only created once), so re-running it
   * on the media query's own change event is the whole of the handling. */
  var booted = false;
  function bootOnce() {
    if (booted || !pointerDevice()) return;
    booted = true;
    boot();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootOnce);
  } else {
    bootOnce();
  }
  try {
    window.matchMedia('(min-width: 750px)').addEventListener('change', bootOnce);
  } catch (e) { /* an old engine keeps the load-time answer */ }
})();

/* ============================================================================
   ROUND 11 (the owner, 2026-10-30): LEFT AND RIGHT IN THE ENLARGED VIEW.
   "When people enlarge a picture, add left and right arrows" — on every device, unlike the zoom above
   (pointer devices only). One step = the zoom dialog's OWN `selectThumbnail`, so the picture, the
   thumbnail ring and the high-resolution swap move together; this file never scrolls anything itself.
   The arrows wrap (last -> first) without animating across every picture in between. A phone also
   steps on a sideways swipe — only while the picture is at 1x, so a pan of a zoomed picture is a pan —
   and a keyboard on ArrowLeft / ArrowRight. Everything is delegated from `document`, because the
   gallery is replaced on every variant change (see `boot` above).
   ========================================================================== */
(function () {
  'use strict';

  function openDialog() {
    return document.querySelector('zoom-dialog dialog[open]');
  }
  function current(dlg) {
    var list = dlg.querySelector('.dialog-zoomed-gallery');
    if (!list || !list.children.length) return { list: list, i: 0, n: 0 };
    var n = list.children.length, i = 0;
    if (list.scrollWidth > list.clientWidth + 1) i = Math.round(list.scrollLeft / list.clientWidth);
    else if (dlg.scrollHeight > dlg.clientHeight + 1) i = Math.round(dlg.scrollTop / dlg.clientHeight);
    return { list: list, i: Math.max(0, Math.min(n - 1, i)), n: n };
  }
  function step(dir) {
    var dlg = openDialog();
    if (!dlg) return;
    var zd = dlg.closest('zoom-dialog'), c = current(dlg);
    if (!zd || !zd.selectThumbnail || c.n < 2) return;
    var next = (c.i + dir + c.n) % c.n;
    var wraps = Math.abs(next - c.i) > 1;
    var still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    zd.selectThumbnail(next, { behavior: wraps || still ? 'instant' : 'smooth' });
  }

  document.addEventListener('click', function (e) {
    var b = e.target.closest && e.target.closest('[data-rz-zoom-step]');
    if (!b) return;
    e.preventDefault();
    e.stopPropagation();
    step(parseInt(b.getAttribute('data-rz-zoom-step'), 10) || 1);
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    if (!openDialog()) return;
    e.preventDefault();
    step(e.key === 'ArrowRight' ? 1 : -1);
  });

  /* THE SWIPE. Horizon's drag-zoom-wrapper cancels the touch's default (so the page cannot scroll under
     the picture), which leaves the browser nothing to swipe with; a passive listener here still sees the
     touches. One finger, mostly sideways, at least 45px, picture at 1x. */
  var touch = null;
  document.addEventListener('touchstart', function (e) {
    var g = e.target.closest && e.target.closest('.dialog-zoomed-gallery');
    if (!g || e.touches.length !== 1) { touch = null; return; }
    touch = { x: e.touches[0].clientX, y: e.touches[0].clientY, t: e.target };
  }, { passive: true });
  document.addEventListener('touchmove', function (e) {
    if (touch && e.touches.length > 1) touch = null;   /* a pinch is never a swipe */
  }, { passive: true });
  document.addEventListener('touchend', function (e) {
    if (!touch || !e.changedTouches.length) return;
    var dx = e.changedTouches[0].clientX - touch.x, dy = e.changedTouches[0].clientY - touch.y;
    var w = touch.t.closest && touch.t.closest('drag-zoom-wrapper');
    touch = null;
    var scale = w ? parseFloat(w.style.getPropertyValue('--drag-zoom-scale')) || 1 : 1;
    if (scale > 1.01) return;
    if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy) * 1.3) return;
    step(dx < 0 ? 1 : -1);
  }, { passive: true });
})();
